import React, {useState, useEffect, useCallback} from "react";
import {FaPlus} from "react-icons/fa";
import {FaGears} from "react-icons/fa6";
import customPronunciationService from "../../services/customPronunciationService";
import EditableTable from "./EditableTable";
import {useLoading} from "../common/LoadingContext";
import SearchBox from "../common/SearchBox";
import CustomPronunciationModal from "./CustomPronunciationModal";
import {useToast} from "../common/ToastContext";
import {getCoreModels} from "../../services/coreModelService";
import {currentUserHasRole} from "../../services/userService";

const CustomPronunciationConsole = () => {
  const [pronunciations, setPronunciations] = useState([]);
  const [addingNew, setAddingNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchParams, setSearchParams] = useState({
    searchTerm: "",
    isRegexMode: localStorage.getItem("search-regex-mode") === "true",
  });
  const [ttsModels, setTtsModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(
    localStorage.getItem("selected-tts-model") || null,
  );
  const defaultItem = {
    phrase: "",
    encoded_phonetic_pronunciation: "",
    phonetic_encoding: "PHONETIC_ENCODING_IPA",
    simple_phonetic_pronunciation: "",
    comment: "",
  };
  const maxPronunciations = 5000;
  const [hasEditAccess, setHasEditAccess] = useState(false);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const {showToast} = useToast();
  const {setIsLoading} = useLoading();

  useEffect(() => {
    const checkAccess = async () => {
      try {
        setHasEditAccess(
          (await currentUserHasRole("prompt-engineer")) ||
          (await currentUserHasRole("admin")),
        );
        setHasAdminAccess(await currentUserHasRole("admin"));
      } catch (error) {
        console.error("Error checking user role:", error);
        setHasEditAccess(false);
      }
    };
    checkAccess();
  }, []);

  useEffect(() => {
    // Fetch custom pronunciations from the backend service
    const fetchPronunciations = async () => {
      try {
        const data = await customPronunciationService.getAll();
        const sortedItems = data.sort((a, b) =>
          a.phrase.localeCompare(b.phrase),
        );
        setPronunciations(sortedItems);
      } catch (error) {
        showToast("Error fetching custom pronunciations", "error");
        console.error("Error fetching custom pronunciations:", error);
      }
    };

    fetchPronunciations();
  }, []);

  useEffect(() => {
    const fetchTtsModels = async () => {
      try {
        const models = await getCoreModels();
        const ttsModels = models.filter(
          (model) => model.type === "tts" && model.provider === "google",
        );
        setTtsModels(ttsModels);
        if (!selectedModel && ttsModels.length > 0) {
          setSelectedModel(ttsModels[0].id);
        }
      } catch (error) {
        console.error("Error fetching TTS models:", error);
      }
    };

    fetchTtsModels();
  }, []);

  const validatePronunciations = useCallback(
    async (newItems, update = false) => {
      const str = update ? "update" : "create";
      const phrasesSet = new Set();
      for (const item of pronunciations) {
        phrasesSet.add(item.phrase.toLowerCase());
      }
      for (const newItem of newItems) {
        if (!update && phrasesSet.has(newItem.phrase.toLowerCase())) {
          showToast(
            `Unable to ${str} pronunciations. A pronunciation for the phrase "${newItem.phrase}" already exists or is defined twice.`,
            "error",
          );
          return false;
        }
        if (!newItem.phrase || newItem.phrase.trim() === "") {
          showToast(
            `Unable to ${str} pronunciations. Phrase cannot be empty.`,
            "error",
          );
          return false;
        }
        if (
          !newItem.encoded_phonetic_pronunciation ||
          newItem.encoded_phonetic_pronunciation.trim() === ""
        ) {
          showToast(
            `Unable to ${str} pronunciations. Encoded phonetic pronunciation cannot be empty for phrase "${newItem.phrase}".`,
            "error",
          );
          return false;
        }
        if (
          !newItem.simple_phonetic_pronunciation ||
          newItem.simple_phonetic_pronunciation.trim() === ""
        ) {
          showToast(
            `Unable to ${str} pronunciations. Simple phonetic pronunciation cannot be empty for phrase "${newItem.phrase}".`,
            "error",
          );
          return false;
        }
        if (
          newItem.phonetic_encoding === undefined ||
          newItem.phonetic_encoding === null ||
          newItem.phonetic_encoding.trim() === ""
        ) {
          showToast(
            `Unable to ${str} pronunciations. Phonetic encoding cannot be empty for phrase "${newItem.phrase}".`,
            "error",
          );
          return false;
        }
        phrasesSet.add(newItem.phrase);
      }
      const invalidPronunciations = (
        await customPronunciationService.validatePronunciations(newItems)
      ).invalid_phrases;
      if (invalidPronunciations.length > 0) {
        showToast(
          `Unable to ${str} pronunciations. The following phrases have invalid phonetic pronunciations: ${invalidPronunciations.join(
            ", ",
          )}. Please review the phonetic pronunciations for these phrases.`,
          "error",
        );
        return false;
      }
      return true;
    },
    [pronunciations],
  );

  const validateAllPronunciations = useCallback(async () => {
    setIsLoading(true);
    const allItems = [...pronunciations];
    const invalidPronunciations = (
      await customPronunciationService.validatePronunciations(allItems)
    ).invalid_phrases;
    if (invalidPronunciations.length > 0) {
      showToast(
        `The following phrases have invalid phonetic pronunciations: ${invalidPronunciations.join(
          ", ",
        )}. Please review the phonetic pronunciations for these phrases.`,
        "error",
      );
      setIsLoading(false);
      return false;
    }
    showToast("All pronunciations are valid!", "success");
    setIsLoading(false);
    return true;
  }, [pronunciations, showToast]);

  const handleUpdatePronunciation = useCallback(async (updatedItem) => {
    try {
      setIsLoading(true);
      if (!(await validatePronunciations([updatedItem], true))) {
        return;
      }
      const savedItem = await customPronunciationService.update(
        updatedItem.id,
        updatedItem,
      );
      setPronunciations((prev) =>
        prev.map((item) => (item.id === savedItem.id ? savedItem : item)),
      );
    } catch (error) {
      showToast(error.message, "error");
      console.error("Error saving custom pronunciation:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleAddPronunciation = async (newItem) => {
    try {
      setIsLoading(true);
      if (Array.isArray(newItem)) {
        if (!(await validatePronunciations(newItem))) {
          return;
        }
        if (pronunciations.length + newItem.length > maxPronunciations) {
          showToast(
            `Unable to create pronunciations. Adding these pronunciations would exceed the maximum limit of ${maxPronunciations}.`,
            "error",
          );
          return;
        }
        const createdItems = await customPronunciationService.bulkCreate({
          pronunciations: newItem,
        });
        setPronunciations((prev) =>
          [...prev, ...createdItems].sort((a, b) =>
            a.phrase.localeCompare(b.phrase),
          ),
        );
      } else {
        if (!(await validatePronunciations([newItem]))) {
          return;
        }
        const createdItem = await customPronunciationService.create(newItem);
        setPronunciations((prev) =>
          [...prev, createdItem].sort((a, b) =>
            a.phrase.localeCompare(b.phrase),
          ),
        );
      }
    } catch (error) {
      showToast(error.message, "error");
      console.error("Error creating custom pronunciation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePronunciation = async (itemToDelete) => {
    try {
      await customPronunciationService.delete(itemToDelete.id);
      setPronunciations((prev) =>
        prev.filter((item) => item.id !== itemToDelete.id),
      );
    } catch (error) {
      showToast("Error deleting custom pronunciation", "error");
      console.error("Error deleting custom pronunciation:", error);
    }
  };

  const handleSearch = (term, regexMode) => {
    setSearchParams({searchTerm: term, isRegexMode: regexMode});
  };

  const handleModelChange = (modelId) => {
    setSelectedModel(modelId);
    localStorage.setItem("selected-tts-model", modelId);
  };

  return (
    <div className="overflow-hidden">
      {/* Search Bar */}
      <div className="absolute top-4 left-[50%] translate-x-[-50%] w-[500px] z-100">
        <div className="flex items-center justify-between">
          <div className="text-sm text-white whitespace-nowrap mr-4">
            {pronunciations.length}/{maxPronunciations} pronunciations
          </div>
          <SearchBox
            value={searchParams.searchTerm}
            onSearch={handleSearch}
            placeholder={"Filter pronunciations ..."}
            filterOptions={[]}
          />
          {hasEditAccess && pronunciations.length < maxPronunciations && (
            <div className="ml-2 flex inline-flex items-center">
              <button
                onClick={() => setAddingNew(true)}
                className="hidden md:inline-flex p-2 text-sm text-white whitespace-nowrap cursor-pointer"
                title="Add New Custom Pronunciation"
                aria-label="Add New Custom Pronunciation"
              >
                <FaPlus size={20} className="inline-block mr-2"/>
                Add New
              </button>
            </div>
          )}
        </div>
        {/* Model Selector */}
        <div className="mt-2 text-sm text-gray-400">
          <span className="flex items-center gap-2 whitespace-nowrap">
            Select TTS Model for Audio Playback:{" "}
            <select
              className="bg-gray-800 text-white p-2 rounded w-full"
              value={selectedModel || "Select TTS Model for Audio Playback"}
              onChange={(e) => handleModelChange(e.target.value)}
            >
              {ttsModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name} ({model.id})
                </option>
              ))}
            </select>
          </span>
        </div>
      </div>
      {/* Content Body */}
      <div className="flex-grow p-6 h-[95vh] max-w-[66vw] mx-auto mt-12 overflow-y-auto">
        {pronunciations.length > 0 ? (
          <EditableTable
            items={pronunciations}
            onEdit={(item) => {
              if (hasEditAccess) {
                setEditing(item);
                return;
              }
              return;
            }}
            searchParams={searchParams}
          />
        ) : (
          <p className="text-gray-500">No custom pronunciations found.</p>
        )}
        {addingNew && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4 text-white">
            <CustomPronunciationModal
              item={defaultItem}
              onEdit={(newItem) => {
                handleAddPronunciation(newItem);
                setAddingNew(false);
              }}
              onCancel={() => setAddingNew(false)}
              isAdd={true}
            />
          </div>
        )}
        {editing && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4 text-white">
            <CustomPronunciationModal
              item={editing}
              onEdit={(newItem) => {
                handleUpdatePronunciation(newItem);
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              onDelete={(itemToDelete) => {
                handleDeletePronunciation(itemToDelete);
                setEditing(null);
              }}
              isAdd={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomPronunciationConsole;
